import {useCallback, useLayoutEffect, useMemo, useState} from 'react';
import {useRouter} from 'next/router';

import {
	ClaimFlow,
	ClaimFormReverseHashMap,
} from '@deezer/next/features/ClaimAccess/constants';

import type {ClaimAccessType} from '@deezer/next/features/ClaimAccess/types';
import {ClaimScreen} from '@deezer/next/features/ClaimAccess/types';

import {getDefaultClaimScreen} from '@deezer/next/features/ClaimAccess/utils';

import {Routes} from '@constants/routes';

type ClaimNavigationUser = Parameters<typeof getDefaultClaimScreen>[0];

type ReplaceRequest = {
	sourceHash: string;
	screen: ClaimScreen;
};

type ClaimNavigationState = {
	pageHash: string | null;
	currentScreen: ClaimScreen;
	claimType: ClaimAccessType | undefined;
	replaceRequest?: ReplaceRequest;
};

const createInitialState = (): ClaimNavigationState => ({
	pageHash: null,
	currentScreen: ClaimScreen.SELECT_CLAIM_TYPE,
	claimType: undefined,
});

const reconcilePageHash = (
	state: ClaimNavigationState,
	pageHash: string,
	user: ClaimNavigationUser,
): ClaimNavigationState => {
	const defaultClaimScreen = getDefaultClaimScreen(user);

	const screen = ClaimFormReverseHashMap[pageHash] || defaultClaimScreen;

	const {type} = ClaimFlow[screen];

	if (state.currentScreen === screen) {
		return {
			...state,
			pageHash,
			replaceRequest: undefined,
		};
	}

	if (type === state.claimType) {
		return {
			...state,
			pageHash,
			currentScreen: screen,
			replaceRequest: undefined,
		};
	}

	return {
		...state,
		pageHash,
		claimType: type,
		currentScreen: defaultClaimScreen,

		replaceRequest: {
			sourceHash: pageHash,
			screen: defaultClaimScreen,
		},
	};
};

export const useClaimNavigation = (user: ClaimNavigationUser) => {
	const router = useRouter();

	const pageHash = useMemo(
		() => '#' + router.asPath.split('#')[1],
		[router.asPath],
	);

	const [navigationState, setNavigationState] =
		useState<ClaimNavigationState>(createInitialState);

	if (navigationState.pageHash !== pageHash) {
		setNavigationState(reconcilePageHash(navigationState, pageHash, user));
	}

	const {currentScreen, claimType, replaceRequest} = navigationState;

	useLayoutEffect(() => {
		if (!replaceRequest || replaceRequest.sourceHash !== pageHash) {
			return;
		}

		const {hash} = ClaimFlow[replaceRequest.screen];

		void router.replace(`${Routes.CLAIM}${hash}`, undefined, {
			shallow: true,
		});
	}, [pageHash, replaceRequest, router]);

	const setClaimType = useCallback(
		(nextClaimType: ClaimAccessType | undefined) => {
			setNavigationState(previous => ({
				...previous,
				claimType: nextClaimType,
			}));
		},
		[],
	);

	const goToScreen = useCallback(
		(screen: ClaimScreen, replace?: boolean) => {
			const {hash} = ClaimFlow[screen];

			setNavigationState(previous => ({
				...previous,
				currentScreen: screen,
				replaceRequest: undefined,
			}));

			if (replace) {
				void router.replace(`${Routes.CLAIM}${hash}`, undefined, {
					shallow: true,
				});

				return;
			}

			void router.push(`${Routes.CLAIM}${hash}`, undefined, {
				shallow: true,
			});
		},
		[router],
	);

	const goBack = useCallback(() => {
		const {previous} = ClaimFlow[currentScreen];

		if (!previous) {
			return;
		}

		setNavigationState(current => ({
			...current,
			currentScreen: previous,
			replaceRequest: undefined,
		}));

		router.back();
	}, [currentScreen, router]);

	const goNext = useCallback(() => {
		const {next} = ClaimFlow[currentScreen];

		if (next) {
			goToScreen(next);
		}
	}, [currentScreen, goToScreen]);

	return {
		currentScreen,

		claimType,
		setClaimType,

		goToScreen,
		goBack,
		goNext,
	};
};

export default useClaimNavigation;
